import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Search as SearchIcon, Refresh, Assignment, EditNote, CheckCircle, EventBusy, FilterList } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { api } from '../../services/apiClient';
import { RouterLink } from '../../utils/linkNav';

// ---------------------------------------------------------------------------
// Sales Contract list -- standing price agreements with customers. Mirrors
// RfqPage.tsx's shape: stat cards computed client-side, a filter bar, a
// single results table, row click -> detail.
// ---------------------------------------------------------------------------

type ContractStatus = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

interface ContractRow {
  id: number;
  contract_number: string;
  customer_id: number | string;
  customer_name?: string;
  contract_date: string;
  valid_from?: string | null;
  valid_to?: string | null;
  total_value?: number | string | null;
  status: ContractStatus;
}

const STATUS_OPTIONS: ContractStatus[] = ['DRAFT', 'ACTIVE', 'EXPIRED', 'CANCELLED'];

const STATUS_TONE: Record<ContractStatus, StatusTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesContractPage: React.FC = () => {
  const navigate = useNavigate();

  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<ContractStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ contracts: ContractRow[]; count: number }>('/sales-contracts')
      .then((res) => setContracts(res.contracts || []))
      .catch((err: any) => setError(err.message || 'Failed to load sales contracts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll]);

  const filtered = contracts.filter((c) => {
    const matchesStatus = !statusFilter || c.status === statusFilter;
    const matchesSearch =
      !search ||
      c.contract_number?.toLowerCase().includes(search.toLowerCase()) ||
      c.customer_name?.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalCount = contracts.length;
  const draftCount = contracts.filter((c) => c.status === 'DRAFT').length;
  const activeCount = contracts.filter((c) => c.status === 'ACTIVE').length;
  const expiredCount = contracts.filter((c) => c.status === 'EXPIRED').length;

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Sales Contracts"
        subtitle="Standing price agreements that sales orders can be raised against"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" component={RouterLink} to="/sales/contracts/new">
              New Contract
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Contracts" value={loading ? null : totalCount} icon={<Assignment />} color="#2563eb" />
        <StatCard title="Draft" value={loading ? null : draftCount} icon={<EditNote />} color="#64748b" />
        <StatCard title="Active" value={loading ? null : activeCount} icon={<CheckCircle />} color="#059669" />
        <StatCard title="Expired" value={loading ? null : expiredCount} icon={<EventBusy />} color="#d97706" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Contract # or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ContractStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Contract', 'Customer', 'Contract Date', 'Valid From', 'Valid To', 'Status'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchAll} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState title="No contracts match these filters" hint="Clear the filters, or create a new contract." />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow
                    key={c.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/sales/contracts/${c.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={c.contract_number} tint="#0284c7" icon={<Assignment fontSize="small" />} />
                    </TableCell>
                    <TableCell>{c.customer_name || '—'}</TableCell>
                    <TableCell>{fmtDate(c.contract_date)}</TableCell>
                    <TableCell>{fmtDate(c.valid_from)}</TableCell>
                    <TableCell>{fmtDate(c.valid_to)}</TableCell>
                    <TableCell>
                      <StatusDot label={c.status} tone={STATUS_TONE[c.status]} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default SalesContractPage;
